import { ArrowLeftIcon, Bluetooth, Download } from "@/components/icons";
import {
  Button,
  Skeleton,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/kit";
import { buildShippingLabelViewModel } from "@/modules/chat-tools/domain/build-shipping-label";
import { useShopSettingsQuery } from "@/modules/chat-tools/presentation/api/queries";
import { downloadElementAsPng } from "@/modules/chat-tools/presentation/lib/download-label-image";
import { isWebBluetoothSupported } from "@/modules/chat-tools/presentation/lib/web-bluetooth";
import { BluetoothPrintDialog } from "@/modules/chat-tools/presentation/ui/BluetoothPrintDialog";
import { ShippingLabelCard } from "@/modules/chat-tools/presentation/ui/ShippingLabelCard";
import { useOrderQuery } from "@/modules/orders/presentation/api/queries";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";

export function ShippingLabelPage() {
  const { id } = useParams({ from: "/app/orders/$id/shipping-label" });
  const nav = useNavigate();
  const labelRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [bluetoothOpen, setBluetoothOpen] = useState(false);
  const bluetoothSupported = isWebBluetoothSupported();
  const orderQuery = useOrderQuery(id);
  const settingsQuery = useShopSettingsQuery();
  const order = orderQuery.data;
  const settings = settingsQuery.data;

  const isLoading = orderQuery.isLoading || settingsQuery.isLoading;

  async function handleDownload() {
    const element = labelRef.current;
    if (!element || !order) return;

    setIsDownloading(true);
    try {
      const safeName = order.orderNumber.replace(/[^\w-]+/g, "-");
      await downloadElementAsPng(element, `${safeName}-shipping-label.png`);
      toast.success("ດາວໂຫລດຮູບປ້າຍແລ້ວ");
    } catch (e) {
      toast.error("ດາວໂຫລດບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setIsDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Skeleton className="h-96 w-full max-w-xs rounded-3xl" />
      </div>
    );
  }

  if (!order || !settings) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-muted-foreground">ບໍ່ພົບອໍເດີ້</p>
        <Button
          variant="outline"
          className="no-print rounded-full"
          onClick={() => nav({ to: "/app/orders" })}
        >
          ກັບໄປລາຍການ
        </Button>
      </div>
    );
  }

  const label = buildShippingLabelViewModel(order, settings);

  const toolbarButtonClass =
    "h-9 shrink-0 rounded-full px-2.5 sm:h-10 sm:px-4";

  const bluetoothButton = (
    <Button
      variant="outline"
      className={toolbarButtonClass}
      disabled={!bluetoothSupported}
      title="ພິມ Bluetooth"
      onClick={() => setBluetoothOpen(true)}
    >
      <Bluetooth data-icon="inline-start" />
      <span className="hidden sm:inline">ພິມ Bluetooth</span>
    </Button>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground">
        <div className="no-print flex items-center justify-between gap-2 border-border border-b bg-background px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
          <Button
            variant="outline"
            className={toolbarButtonClass}
            title="ກັບຄືນ"
            onClick={() => nav({ to: "/app/orders/$id", params: { id } })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            <span className="hidden sm:inline">ກັບຄືນ</span>
          </Button>
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
            <Button
              variant="outline"
              className={toolbarButtonClass}
              title="ດາວໂຫລດຮູບ"
              isLoading={isDownloading}
              onClick={handleDownload}
            >
              <Download data-icon="inline-start" />
              <span className="hidden sm:inline">ດາວໂຫລດຮູບ</span>
            </Button>
            <Button
              variant="outline"
              className={toolbarButtonClass}
              title="ພິມ"
              onClick={() => window.print()}
            >
              ພິມ
            </Button>
            {bluetoothSupported ? (
              bluetoothButton
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{bluetoothButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Browser ນີ້ບໍ່ຮອງຮັບ Bluetooth — ໃຊ້ Chrome ຫຼື ດາວໂຫລດຮູບ
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center overflow-y-auto p-4 sm:p-6 print:p-0">
          <ShippingLabelCard
            ref={labelRef}
            id="shipping-label-print"
            label={label}
          />
        </div>
      </div>

      <BluetoothPrintDialog
        open={bluetoothOpen}
        onOpenChange={setBluetoothOpen}
        label={label}
        labelElement={labelRef.current}
        onSuccess={() => {
          toast.success("ພິມປ້າຍແລ້ວ");
        }}
        onError={(error) => {
          toast.error("ພິມ Bluetooth ບໍ່ສຳເລັດ", {
            description: `${error.message} — ລອງດາວໂຫລດຮູບແທນ`,
          });
        }}
      />
    </>
  );
}
